import type { ReactNode } from 'react';
import { Button } from '../../../components/ui/Button.js';
import { Modal } from '../../../components/ui/Modal.js';
import { type FieldChange, changeParts, formatMyt } from '../settingsFormat.js';
import type { Mode } from '../settingsTypes.js';

/**
 * 356: the shared parts of design 1b. Tokens only (`admin/src/index.css`, DESIGN-mongodb-admin.md).
 */

export type ChipTone = 'on' | 'off' | 'count' | 'alert';

const CHIP_TONES: Record<ChipTone, string> = {
  on: 'bg-semantic-success-bg text-semantic-success-text',
  off: 'bg-surface text-slate',
  count: 'bg-semantic-warning-bg text-semantic-warning-text',
  alert: 'bg-semantic-danger-bg text-semantic-danger-text',
};

/** The saved state of a setting, e.g. "ON since 5 Oct, 06:35" (FR-003). */
export function StatusChip({
  tone,
  children,
  since,
  testId,
}: {
  tone: ChipTone;
  children: ReactNode;
  since?: string | null;
  testId?: string;
}) {
  return (
    <span
      data-testid={testId}
      className={[
        'inline-flex h-6 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-semibold',
        CHIP_TONES[tone],
      ].join(' ')}
    >
      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current" />
      {children}
      {since ? ` since ${formatMyt(since)}` : ''}
    </span>
  );
}

export function modeTone(mode: Mode): ChipTone {
  return mode === 'on' ? 'on' : mode === 'count' ? 'count' : 'off';
}

export const MODE_CHIP: Record<Mode, string> = { off: 'OFF', count: 'Count only', on: 'ON' };

/** One Off / Count only / On control for both automations (FR-004). */
export function ModeControl({
  label,
  name,
  value,
  onChange,
  help,
}: {
  label: string;
  /** The radio group's name; unique per control on the page. */
  name: string;
  value: Mode;
  onChange: (mode: Mode) => void;
  help: Record<Mode, string>;
}) {
  const options: Array<{ value: Mode; label: string }> = [
    { value: 'off', label: 'Off' },
    { value: 'count', label: 'Count only' },
    { value: 'on', label: 'On' },
  ];
  return (
    <div className="flex flex-col gap-1.5">
      <span aria-hidden="true" className="text-sm font-medium text-charcoal">
        {label}
      </span>
      <fieldset className="inline-grid w-max max-w-full auto-cols-[minmax(96px,max-content)] grid-flow-col gap-1 rounded-lg border border-hairline-strong bg-surface p-1">
        <legend className="sr-only">{label}</legend>
        {options.map((o) => {
          const selected = value === o.value;
          return (
            <label
              key={o.value}
              className={[
                'cursor-pointer rounded-md px-3.5 py-1.5 text-center text-sm transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary',
                selected
                  ? `bg-canvas font-semibold shadow-sm ${
                      o.value === 'on'
                        ? 'text-semantic-success-text'
                        : o.value === 'count'
                          ? 'text-semantic-warning-text'
                          : 'text-ink'
                    }`
                  : 'font-medium text-slate hover:text-ink',
              ].join(' ')}
            >
              <input
                type="radio"
                className="sr-only"
                name={name}
                value={o.value}
                checked={selected}
                onChange={() => onChange(o.value)}
              />
              {o.label}
            </label>
          );
        })}
      </fieldset>
      <p className="text-xs text-steel">{help[value]}</p>
    </div>
  );
}

/** The card frame: title, status chip, description, body, footer. Highlighted while it holds edits. */
export function SettingCard({
  title,
  chip,
  description,
  dirty,
  refusal,
  testId,
  children,
  footer,
}: {
  title: string;
  chip?: ReactNode;
  description?: ReactNode;
  dirty: boolean;
  /** The server's reason for refusing the last save of this card. */
  refusal?: string | null;
  testId?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <section
      data-testid={testId}
      data-dirty={dirty ? 'true' : 'false'}
      aria-label={title}
      className={[
        'flex flex-col gap-4 rounded-xl border bg-canvas p-5 shadow-[0px_1px_2px_0px_rgba(0,30,43,0.04)]',
        dirty ? 'border-semantic-warning-text ring-2 ring-brand-yellow-soft' : 'border-hairline',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-base font-semibold text-ink">{title}</h2>
        {chip}
      </div>
      {description && <div className="max-w-prose text-sm text-steel">{description}</div>}
      {children}
      {refusal && (
        <p role="alert" className="text-sm text-semantic-danger-text">
          {refusal}
        </p>
      )}
      {footer && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-hairline-soft pt-3">
          {footer}
        </div>
      )}
    </section>
  );
}

/** "Unsaved: Payment hold 20 min → 5 min" for a card holding edits (FR-006). */
export function PendingChanges({ setting, changes }: { setting: string; changes: FieldChange[] }) {
  if (changes.length === 0) return null;
  return (
    <ul data-testid={`pending-${setting}`} className="flex flex-col gap-0.5 text-sm text-charcoal">
      {changes.map((c) => {
        const p = changeParts(setting, c);
        return (
          <li key={c.field}>
            <span className="font-semibold text-semantic-warning-text">Unsaved:</span> {p.label}{' '}
            <s className="text-muted">{p.from}</s> → {p.to}
          </li>
        );
      })}
    </ul>
  );
}

/** The bar at the top of a tab with unsaved edits: count, Discard, Review and save (FR-006). */
export function UnsavedBar({
  count,
  saving,
  onDiscard,
  onSave,
}: {
  count: number;
  saving: boolean;
  onDiscard: () => void;
  onSave: () => void;
}) {
  if (count === 0) return null;
  return (
    <div
      aria-live="polite"
      data-testid="unsaved-bar"
      className="sticky bottom-4 z-20 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-semantic-warning-text bg-brand-yellow-soft px-4 py-2.5 text-sm text-charcoal shadow-lg"
    >
      <span>
        <b className="text-semantic-warning-text">
          {count} unsaved {count === 1 ? 'change' : 'changes'}
        </b>{' '}
        on this tab. Save them, or they are lost when you leave.
      </span>
      <span className="flex gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onDiscard} disabled={saving}>
          Discard
        </Button>
        <Button type="button" size="sm" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : 'Review and save'}
        </Button>
      </span>
    </div>
  );
}

/** Asked before unsaved edits are lost: by a tab switch, a menu link or a back button (FR-008). */
export function LeaveDialog({
  open,
  count,
  onStay,
  onDiscard,
}: {
  open: boolean;
  count: number;
  onStay: () => void;
  onDiscard: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onStay}
      title="Leave without saving?"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onStay}>
            Stay
          </Button>
          <Button type="button" variant="danger" onClick={onDiscard}>
            Discard changes
          </Button>
        </div>
      }
    >
      <p className="text-sm text-charcoal">
        {count === 1 ? 'One change is' : `${count} changes are`} not saved. Leaving now loses{' '}
        {count === 1 ? 'it' : 'them'}.
      </p>
    </Modal>
  );
}
